import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { GAME_TYPE } from "@/lib/api/games";
import { Zap, Brain, TrendingUp } from "lucide-react";

interface StartGameDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onStartGame: (gameType: GAME_TYPE) => void;
  isLoading?: boolean;
}

const GAME_OPTIONS = [
  {
    value: GAME_TYPE.TAP_COUNTER,
    label: "Tap Counter",
    description: "Test your reflexes by tapping as fast as you can",
    icon: Zap,
    color: "text-emerald-600",
  },
  {
    value: GAME_TYPE.SEQUENCE_REPEATER,
    label: "Sequence Repeater",
    description: "Train your memory by repeating sequences",
    icon: Brain,
    color: "text-purple-600",
  },
  {
    value: GAME_TYPE.ASCENT,
    label: "Ascent",
    description: "Climb to new heights and reach the peak",
    icon: TrendingUp,
    color: "text-blue-600",
  },
];

export function StartGameDialog({
  open,
  onOpenChange,
  onStartGame,
  isLoading,
}: StartGameDialogProps) {
  const [selectedGame, setSelectedGame] = useState<GAME_TYPE | "">("");

  const handleStart = () => {
    if (selectedGame) {
      onStartGame(selectedGame as GAME_TYPE);
      setSelectedGame("");
    }
  };

  const selectedGameOption = GAME_OPTIONS.find(
    (option) => option.value === selectedGame,
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Start a Game</DialogTitle>
          <DialogDescription>
            Select a game to start playing. Your teen will see this activity in
            the chat.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="game-type">Game Type</Label>
            <Select
              value={selectedGame}
              onValueChange={(value) => setSelectedGame(value as GAME_TYPE)}
            >
              <SelectTrigger id="game-type">
                <SelectValue placeholder="Choose a game..." />
              </SelectTrigger>
              <SelectContent>
                {GAME_OPTIONS.map((option) => {
                  const Icon = option.icon;
                  return (
                    <SelectItem key={option.value} value={option.value}>
                      <div className="flex items-center gap-2">
                        <Icon className={`h-4 w-4 ${option.color}`} />
                        <span>{option.label}</span>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {selectedGameOption && (
            <div className="rounded-lg border bg-gray-50 p-4">
              <div className="mb-2 flex items-center gap-2">
                <selectedGameOption.icon
                  className={`h-5 w-5 ${selectedGameOption.color}`}
                />
                <h4 className="font-semibold">{selectedGameOption.label}</h4>
              </div>
              <p className="text-sm text-gray-600">
                {selectedGameOption.description}
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleStart}
            disabled={!selectedGame || isLoading}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isLoading ? "Starting..." : "Start Game"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
